import { FC, useEffect, useRef, useCallback, useState } from 'react';
import { Canvas } from 'fabric';
import { driver } from 'driver.js';
import { createRoot, Root } from 'react-dom/client';
import { useShallow } from 'zustand/react/shallow';

import { useMediaHubModalsStore } from '@app/widgets/canvas/store';

import { useLargestCanvasImage } from '../../hooks/use-largest-canvas-image';
import { useOnboardingStore } from '../../model/onboarding-store';
import { useOnboardingState } from '../../hooks/use-onboarding-state';
import { waitForElement } from '../../utils/wait-for-element';
import type { ImageGenTourStep } from '../../model/onboarding.types';

import { IMAGE_GEN_TOUR_ID } from './image-gen-tour.config';
import { ImageGenPopover } from './image-gen-popover';

import 'driver.js/dist/driver.css';

interface ImageGenTourProps {
  canvas: Canvas | null;
  canvasId: string;
  containerRef: React.RefObject<HTMLDivElement>;
}

// Steps inside Media Hub - if hub closes mid-tour, cancel gracefully
const MEDIA_HUB_STEPS: ImageGenTourStep[] = [
  'generate_tab',
  'prompt_textarea',
  'send_button',
  'wait_and_save',
];

// Selector map for each step that targets a DOM element by data-onboarding-id
const STEP_SELECTORS: Partial<Record<ImageGenTourStep, string>> = {
  change_image: '[data-onboarding-id="change-image-btn"]',
  add_to_slide: '[data-onboarding-id="add-to-slide-btn"]',
  add_image_item: '[data-onboarding-id="add-image-btn"]',
  generate_tab: '[data-onboarding-id="generate-tab"]',
  prompt_textarea: '[data-onboarding-id="prompt-textarea"]',
  send_button: '[data-onboarding-id="send-btn"]',
  wait_and_save: '[data-onboarding-id="save-btn"]',
  fab_hint: '[data-onboarding-id="onboarding-fab"]',
};

// i18n keys for each step popover
const STEP_I18N: Partial<Record<ImageGenTourStep, { titleKey: string; descriptionKey: string }>> = {
  change_image: {
    titleKey: 'onboarding.tours.imageGen.changeImage.title',
    descriptionKey: 'onboarding.tours.imageGen.changeImage.description',
  },
  add_to_slide: {
    titleKey: 'onboarding.tours.imageGen.addToSlide.title',
    descriptionKey: 'onboarding.tours.imageGen.addToSlide.description',
  },
  add_image_item: {
    titleKey: 'onboarding.tours.imageGen.addImageItem.title',
    descriptionKey: 'onboarding.tours.imageGen.addImageItem.description',
  },
  generate_tab: {
    titleKey: 'onboarding.tours.imageGen.generateTab.title',
    descriptionKey: 'onboarding.tours.imageGen.generateTab.description',
  },
  prompt_textarea: {
    titleKey: 'onboarding.tours.imageGen.promptTextarea.title',
    descriptionKey: 'onboarding.tours.imageGen.promptTextarea.description',
  },
  send_button: {
    titleKey: 'onboarding.tours.imageGen.sendButton.title',
    descriptionKey: 'onboarding.tours.imageGen.sendButton.description',
  },
  wait_and_save: {
    titleKey: 'onboarding.tours.imageGen.waitAndSave.title',
    descriptionKey: 'onboarding.tours.imageGen.waitAndSave.description',
  },
  fab_hint: {
    titleKey: 'onboarding.tours.imageGen.fabHint.title',
    descriptionKey: 'onboarding.tours.imageGen.fabHint.description',
  },
};

// Popover side config per step
const STEP_POPOVER_SIDE: Partial<Record<ImageGenTourStep, 'top' | 'bottom' | 'left' | 'right'>> = {
  change_image: 'bottom',
  add_to_slide: 'bottom',
  add_image_item: 'bottom',
  generate_tab: 'right',
  prompt_textarea: 'right',
  send_button: 'left',
  wait_and_save: 'left',
  fab_hint: 'left',
};

// Custom popover offset per step (default is 12)
const STEP_POPOVER_OFFSET: Partial<Record<ImageGenTourStep, number>> = {
  change_image: 20,
  wait_and_save: 40,
};

/**
 * Checks if a Fabric.js object is a replaceable content image
 * (photo or object media_type, not a marker/icon/metadata).
 */
function isReplaceableImage(obj: { type?: string; media_type?: string }): boolean {
  if (!obj.type || obj.type.toLowerCase() !== 'image') return false;
  const mt = obj.media_type?.toLowerCase();
  return mt === 'photo' || mt === 'object';
}

export const ImageGenTour: FC<ImageGenTourProps> = ({ canvas, canvasId, containerRef }) => {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const popoverRootRef = useRef<Root | null>(null);
  const hasAutoShownRef = useRef(false);
  // Tracks whether the Image Hub was seen open during the current Media Hub step.
  const hubWasOpenRef = useRef(false);
  // Tracks whether the user ever reached a Media Hub step during this tour session.
  // If true, we skip the fab_hint step (user already knows about the feature).
  const hubWasVisitedRef = useRef(false);

  // State machine
  const [currentStep, setCurrentStep] = useState<ImageGenTourStep>('initial');
  const [isTourRunning, setIsTourRunning] = useState(false);

  // We still need this to know whether the slide HAS replaceable images (for auto-show)
  const largestImage = useLargestCanvasImage(canvas);

  const { activeTourId } = useOnboardingStore(
    useShallow((state) => ({
      activeTourId: state.activeTourId,
    })),
  );

  const { dismissTour, stopActiveTour, isTourDismissed, startTour } = useOnboardingState();

  const { isImageHubOpen, setInitialTab, setInitialTransparentBackground } = useMediaHubModalsStore(
    useShallow((state) => ({
      isImageHubOpen: state.isImageHubOpen,
      setInitialTab: state.setInitialTab,
      setInitialTransparentBackground: state.setInitialTransparentBackground,
    })),
  );

  const destroyDriver = useCallback(() => {
    if (popoverRootRef.current) {
      const root = popoverRootRef.current;
      popoverRootRef.current = null;
      setTimeout(() => root.unmount(), 0);
    }
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
  }, []);

  const cancelTour = useCallback(() => {
    destroyDriver();
    setCurrentStep('initial');
    setIsTourRunning(false);
    hubWasOpenRef.current = false;
    hubWasVisitedRef.current = false;
    stopActiveTour();
  }, [destroyDriver, stopActiveTour]);

  const completeTour = useCallback(() => {
    dismissTour(IMAGE_GEN_TOUR_ID);
    destroyDriver();
    setCurrentStep('initial');
    setIsTourRunning(false);
    hubWasOpenRef.current = false;
    hubWasVisitedRef.current = false;
  }, [dismissTour, destroyDriver]);

  // Dismiss the tour. If user already visited Media Hub, complete immediately
  // (they already know about the feature). Otherwise show fab_hint.
  const handleDismiss = useCallback(() => {
    if (hubWasVisitedRef.current) {
      completeTour();
    } else {
      dismissTour(IMAGE_GEN_TOUR_ID);
      destroyDriver();
      setCurrentStep('fab_hint');
      hubWasOpenRef.current = false;
    }
  }, [completeTour, dismissTour, destroyDriver]);

  // Skip = same as dismiss (transition to fab_hint)
  const handleSkip = useCallback(() => {
    handleDismiss();
  }, [handleDismiss]);

  // Close the initial balloon - dismiss the tour and show FAB hint.
  const handleCloseInitialBalloon = useCallback(() => {
    handleDismiss();
  }, [handleDismiss]);

  // Helper to get or create a driver instance
  const getOrCreateDriver = useCallback(() => {
    if (driverRef.current) return driverRef.current;

    const driverInstance = driver({
      animate: true,
      showProgress: false,
      showButtons: [],
      allowClose: false,
      overlayColor: 'rgba(0, 0, 0, 0.75)',
      stagePadding: 8,
      stageRadius: 8,
      disableActiveInteraction: false,
    });

    driverRef.current = driverInstance;
    return driverInstance;
  }, []);

  // Renders a custom popover inside driver.js popover wrapper
  const renderPopover = useCallback((wrapper: HTMLElement, popoverContent: React.ReactNode) => {
    wrapper.innerHTML = '';
    wrapper.style.padding = '0';
    wrapper.style.background = 'transparent';
    wrapper.style.borderRadius = '12px';
    wrapper.style.border = 'none';
    wrapper.style.boxShadow = 'none';

    // Prevent Radix Dialog from detecting clicks on the tour popover as "outside" clicks.
    wrapper.addEventListener('pointerdown', (e) => e.stopPropagation());

    const container = document.createElement('div');
    wrapper.appendChild(container);

    if (popoverRootRef.current) {
      popoverRootRef.current.unmount();
    }
    const root = createRoot(container);
    popoverRootRef.current = root;
    root.render(popoverContent);
  }, []);

  // Highlight an element with a custom popover
  const highlightElement = useCallback(
    (
      element: HTMLElement,
      popoverContent: React.ReactNode,
      side: string = 'right',
      offset: number = 12,
    ) => {
      const driverInstance = getOrCreateDriver();

      driverInstance.highlight({
        element,
        popover: {
          title: '',
          description: '',
          side: side as 'top' | 'bottom' | 'left' | 'right',
          align: 'start',
          onPopoverRender: (popover) => {
            renderPopover(popover.wrapper, popoverContent);
            // Driver.js miscalculates popover position for elements near viewport edges.
            // Override with manual fixed positioning based on element's bounding rect.
            // If the popover overflows the viewport on the preferred side, fall back to 'top'.
            requestAnimationFrame(() => {
              const elRect = element.getBoundingClientRect();
              const pw = popover.wrapper;
              const vw = window.innerWidth;
              const vh = window.innerHeight;
              pw.style.position = 'fixed';
              pw.style.margin = '0';

              const applyPosition = (s: string) => {
                switch (s) {
                  case 'bottom':
                    pw.style.top = `${elRect.bottom + offset}px`;
                    pw.style.left = `${elRect.left}px`;
                    break;
                  case 'top':
                    pw.style.top = `${elRect.top - pw.offsetHeight - offset}px`;
                    pw.style.left = `${elRect.left}px`;
                    break;
                  case 'right':
                    pw.style.top = `${elRect.top}px`;
                    pw.style.left = `${elRect.right + offset}px`;
                    break;
                  case 'left':
                    pw.style.top = `${elRect.bottom - pw.offsetHeight}px`;
                    pw.style.left = `${elRect.left - pw.offsetWidth - offset}px`;
                    break;
                }
              };

              const fitsOnSide = (s: string): boolean => {
                switch (s) {
                  case 'right':
                    return elRect.right + offset + pw.offsetWidth <= vw;
                  case 'left':
                    return elRect.left - pw.offsetWidth - offset >= 0;
                  case 'bottom':
                    return elRect.bottom + offset + pw.offsetHeight <= vh;
                  case 'top':
                    return elRect.top - pw.offsetHeight - offset >= 0;
                  default:
                    return true;
                }
              };

              if (fitsOnSide(side)) {
                applyPosition(side);
              } else {
                // Fallback order: try opposite side, then top, then bottom
                const fallbacks: Record<string, string[]> = {
                  right: ['left', 'top', 'bottom'],
                  left: ['right', 'top', 'bottom'],
                  bottom: ['top', 'left', 'right'],
                  top: ['bottom', 'left', 'right'],
                };
                const chosen = fallbacks[side]?.find((fb) => fitsOnSide(fb)) || 'top';
                applyPosition(chosen);
              }
            });
          },
        },
      });
    },
    [getOrCreateDriver, renderPopover],
  );

  // Auto-show logic: show tour on first visit when slide has replaceable images
  useEffect(() => {
    if (hasAutoShownRef.current) return;
    if (!largestImage) return;
    if (isImageHubOpen) return;
    if (isTourDismissed(IMAGE_GEN_TOUR_ID)) return;
    if (activeTourId !== null) return;

    const timeoutId = setTimeout(() => {
      hasAutoShownRef.current = true;
      startTour(IMAGE_GEN_TOUR_ID);
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [largestImage, isImageHubOpen, isTourDismissed, activeTourId, startTour]);

  // When tour becomes active, start the state machine
  useEffect(() => {
    if (activeTourId === IMAGE_GEN_TOUR_ID && !isTourRunning) {
      setCurrentStep('initial');
      setIsTourRunning(true);
    } else if (activeTourId !== IMAGE_GEN_TOUR_ID && isTourRunning) {
      // Don't reset if we're in fab_hint (tour dismissed but hint still pending)
      if (currentStep === 'fab_hint') return;
      destroyDriver();
      setCurrentStep('initial');
      setIsTourRunning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTourId]);

  // Main state machine effect - runs on step changes
  useEffect(() => {
    if (!isTourRunning) return;
    // fab_hint runs after dismissTour, so activeTourId may already be null
    if (currentStep !== 'fab_hint' && activeTourId !== IMAGE_GEN_TOUR_ID) return;

    let cancelled = false;
    let cleanupFn: (() => void) | null = null;

    const runStep = async () => {
      // Track if user reached any Media Hub step (opened the modal)
      if (MEDIA_HUB_STEPS.includes(currentStep)) {
        hubWasVisitedRef.current = true;
      }

      switch (currentStep) {
        case 'initial': {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const canvasWrapper = (canvas as any)?.wrapperEl as HTMLDivElement | undefined;
          const targetEl = canvasWrapper || containerRef.current;
          if (!targetEl) return;

          if (!largestImage) {
            // Path B: No images on canvas - show "add image" message and go to add_to_slide
            highlightElement(
              targetEl,
              <ImageGenPopover
                variant="step"
                titleKey="onboarding.tours.imageGen.noImagesInitial.title"
                descriptionKey="onboarding.tours.imageGen.noImagesInitial.description"
                onDismiss={handleCloseInitialBalloon}
                actionLabelKey="onboarding.tours.imageGen.next"
                onAction={() => {
                  if (!cancelled) {
                    destroyDriver();
                    setCurrentStep('add_to_slide');
                  }
                }}
              />,
              'right',
              12,
            );
          } else {
            // Path A: Has images - highlight canvas, wait for selection
            highlightElement(
              targetEl,
              <ImageGenPopover variant="initial" onDismiss={handleCloseInitialBalloon} />,
              'right',
              12,
            );

            // Listen for canvas selection events
            if (canvas) {
              let selectionTimeout: ReturnType<typeof setTimeout> | null = null;

              const handleSelection = () => {
                if (cancelled) return;
                const activeObjects = canvas.getActiveObjects();
                if (activeObjects.length !== 1) return;
                const obj = activeObjects[0];
                if (!isReplaceableImage(obj)) return;

                if (selectionTimeout) clearTimeout(selectionTimeout);
                selectionTimeout = setTimeout(() => {
                  if (cancelled) return;
                  destroyDriver();
                  setCurrentStep('change_image');
                }, 400);
              };

              canvas.on('selection:created', handleSelection);
              canvas.on('selection:updated', handleSelection);

              cleanupFn = () => {
                canvas.off('selection:created', handleSelection);
                canvas.off('selection:updated', handleSelection);
                if (selectionTimeout) clearTimeout(selectionTimeout);
              };
            }
          }
          break;
        }

        case 'change_image': {
          // Wait for the "Change Image" button to appear in toolbar
          const selector = STEP_SELECTORS[currentStep]!;
          const el = await waitForElement(selector, 5000);
          if (cancelled || !el) {
            if (!cancelled) cancelTour();
            return;
          }

          const i18n = STEP_I18N[currentStep]!;
          const side = STEP_POPOVER_SIDE[currentStep] || 'left';
          const popoverOffset = STEP_POPOVER_OFFSET[currentStep] ?? 12;
          const popoverNode = (
            <ImageGenPopover
              variant="step"
              titleKey={i18n.titleKey}
              descriptionKey={i18n.descriptionKey}
              onDismiss={handleDismiss}
              onSkip={handleSkip}
            />
          );

          highlightElement(el, popoverNode, side, popoverOffset);

          // Listen for user clicking the "Change Image" button
          const handleClick = () => {
            if (cancelled) return;
            destroyDriver();
            // Pre-set Media Hub to Generate tab
            setInitialTab('generate');
            // Check if the selected image is an object type for transparent bg
            const activeObj = canvas?.getActiveObject();
            if (activeObj?.media_type?.toLowerCase() === 'object') {
              setInitialTransparentBackground(true);
            }
            setCurrentStep('generate_tab');
          };
          el.addEventListener('click', handleClick, { once: true });
          cleanupFn = () => el.removeEventListener('click', handleClick);
          break;
        }

        case 'add_to_slide': {
          // Path B: highlight the "Add to Slide" button in the top panel
          const addSelector = STEP_SELECTORS[currentStep]!;
          const addEl = await waitForElement(addSelector, 5000);
          if (cancelled || !addEl) {
            if (!cancelled) cancelTour();
            return;
          }

          const addI18n = STEP_I18N[currentStep]!;
          highlightElement(
            addEl,
            <ImageGenPopover
              variant="step"
              titleKey={addI18n.titleKey}
              descriptionKey={addI18n.descriptionKey}
              onDismiss={handleDismiss}
              onSkip={handleSkip}
            />,
            STEP_POPOVER_SIDE[currentStep] || 'bottom',
            STEP_POPOVER_OFFSET[currentStep] ?? 12,
          );

          // Wait for user to click the button (opens dropdown)
          const handleAddClick = () => {
            if (cancelled) return;
            destroyDriver();
            setCurrentStep('add_image_item');
          };
          addEl.addEventListener('click', handleAddClick, { once: true });
          cleanupFn = () => addEl.removeEventListener('click', handleAddClick);
          break;
        }

        case 'add_image_item': {
          // Path B: highlight "Add Image" in the dropdown
          const itemSelector = STEP_SELECTORS[currentStep]!;
          const itemEl = await waitForElement(itemSelector, 5000);
          if (cancelled || !itemEl) {
            if (!cancelled) cancelTour();
            return;
          }

          const itemI18n = STEP_I18N[currentStep]!;
          highlightElement(
            itemEl,
            <ImageGenPopover
              variant="step"
              titleKey={itemI18n.titleKey}
              descriptionKey={itemI18n.descriptionKey}
              onDismiss={handleDismiss}
              onSkip={handleSkip}
            />,
            STEP_POPOVER_SIDE[currentStep] || 'bottom',
            STEP_POPOVER_OFFSET[currentStep] ?? 12,
          );

          // Wait for user to click "Add Image" (opens Media Hub)
          const handleItemClick = () => {
            if (cancelled) return;
            destroyDriver();
            setInitialTab('generate');
            setCurrentStep('generate_tab');
          };
          itemEl.addEventListener('click', handleItemClick, { once: true });
          cleanupFn = () => itemEl.removeEventListener('click', handleItemClick);
          break;
        }

        case 'generate_tab': {
          // Tab is already pre-selected via setInitialTab('generate').
          // Show an info popover with "Next" button.
          const genSelector = STEP_SELECTORS[currentStep]!;
          const genEl = await waitForElement(genSelector, 5000);
          if (cancelled || !genEl) {
            if (!cancelled) cancelTour();
            return;
          }

          const genI18n = STEP_I18N[currentStep]!;
          highlightElement(
            genEl,
            <ImageGenPopover
              variant="step"
              titleKey={genI18n.titleKey}
              descriptionKey={genI18n.descriptionKey}
              onDismiss={handleDismiss}
              onSkip={handleSkip}
              actionLabelKey="onboarding.tours.imageGen.next"
              onAction={() => {
                if (!cancelled) setCurrentStep('prompt_textarea');
              }}
            />,
            STEP_POPOVER_SIDE[currentStep] || 'bottom',
            STEP_POPOVER_OFFSET[currentStep] ?? 12,
          );
          break;
        }

        case 'prompt_textarea': {
          const selector = STEP_SELECTORS[currentStep]!;
          const el = await waitForElement(selector, 5000);
          if (cancelled || !el) {
            if (!cancelled) cancelTour();
            return;
          }

          const i18n = STEP_I18N[currentStep]!;
          highlightElement(
            el,
            <ImageGenPopover
              variant="step"
              titleKey={i18n.titleKey}
              descriptionKey={i18n.descriptionKey}
              onDismiss={handleDismiss}
              onSkip={handleSkip}
              actionLabelKey="onboarding.tours.imageGen.next"
              onAction={() => {
                if (!cancelled) setCurrentStep('send_button');
              }}
            />,
            STEP_POPOVER_SIDE[currentStep] || 'left',
            STEP_POPOVER_OFFSET[currentStep] ?? 12,
          );
          break;
        }

        case 'send_button': {
          const selector = STEP_SELECTORS[currentStep]!;
          const el = await waitForElement(selector, 5000);
          if (cancelled || !el) {
            if (!cancelled) cancelTour();
            return;
          }

          const i18n = STEP_I18N[currentStep]!;
          highlightElement(
            el,
            <ImageGenPopover
              variant="step"
              titleKey={i18n.titleKey}
              descriptionKey={i18n.descriptionKey}
              onDismiss={handleDismiss}
              onSkip={handleSkip}
              actionLabelKey="onboarding.tours.imageGen.next"
              onAction={() => {
                if (!cancelled) setCurrentStep('wait_and_save');
              }}
            />,
            STEP_POPOVER_SIDE[currentStep] || 'left',
            STEP_POPOVER_OFFSET[currentStep] ?? 12,
          );
          break;
        }

        case 'wait_and_save': {
          const selector = STEP_SELECTORS[currentStep]!;
          const el = await waitForElement(selector, 5000);
          if (cancelled || !el) {
            if (!cancelled) cancelTour();
            return;
          }

          const i18n = STEP_I18N[currentStep]!;
          highlightElement(
            el,
            <ImageGenPopover
              variant="step"
              titleKey={i18n.titleKey}
              descriptionKey={i18n.descriptionKey}
              onDismiss={handleDismiss}
              onSkip={handleSkip}
              actionLabelKey="onboarding.tours.imageGen.done"
              onAction={() => {
                if (cancelled) return;
                completeTour();
              }}
            />,
            STEP_POPOVER_SIDE[currentStep] || 'top',
            STEP_POPOVER_OFFSET[currentStep] ?? 12,
          );
          break;
        }

        case 'fab_hint': {
          // Wait for Image Hub to close, then highlight the FAB
          if (isImageHubOpen) {
            // Hub is still open - do nothing, the useEffect watching
            // isImageHubOpen will re-trigger this step when hub closes
            break;
          }

          const fabSelector = STEP_SELECTORS[currentStep]!;
          const fabEl = await waitForElement(fabSelector, 5000);
          if (cancelled || !fabEl) {
            if (!cancelled) completeTour();
            return;
          }

          const fabI18n = STEP_I18N[currentStep]!;
          highlightElement(
            fabEl,
            <ImageGenPopover
              variant="step"
              titleKey={fabI18n.titleKey}
              descriptionKey={fabI18n.descriptionKey}
              onDismiss={() => {
                completeTour();
              }}
              actionLabelKey="onboarding.tours.imageGen.gotIt"
              onAction={() => {
                completeTour();
              }}
            />,
            STEP_POPOVER_SIDE[currentStep] || 'top',
            STEP_POPOVER_OFFSET[currentStep] ?? 12,
          );
          break;
        }

        case 'completed': {
          completeTour();
          break;
        }
      }
    };

    runStep();

    return () => {
      cancelled = true;
      if (cleanupFn) cleanupFn();
    };
    // isImageHubOpen is included so fab_hint step re-runs when hub closes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isTourRunning, isImageHubOpen]);

  // Cancel tour if Media Hub closes AFTER it was open during a Media Hub step.
  // For fab_hint step, hub closing triggers the FAB highlight.
  useEffect(() => {
    if (!isTourRunning) return;

    if (currentStep === 'fab_hint') {
      // fab_hint waits for hub to close - re-run the step effect when it does
      return;
    }

    if (!MEDIA_HUB_STEPS.includes(currentStep)) {
      hubWasOpenRef.current = false;
      return;
    }

    if (isImageHubOpen) {
      hubWasOpenRef.current = true;
    } else if (hubWasOpenRef.current) {
      // Hub closed during a media hub step - user already saw the feature, complete
      hubWasOpenRef.current = false;
      completeTour();
    }
  }, [isImageHubOpen, currentStep, isTourRunning, completeTour]);

  // Reset tour when canvas changes (slide switch)
  useEffect(() => {
    if (isTourRunning) {
      cancelTour();
    }
    hasAutoShownRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasId]);

  // The component does not render any portal overlay anymore.
  // The initial step highlights the canvas container directly via containerRef.
  return null;
};
